/**
 * Referral codes, remembered.
 *
 * The code used to live only in the URL of the page the visitor happened to be
 * looking at. An agent sends `…/register?ref=ABC123`; the client opens it, has
 * a look, closes the tab, and comes back two days later through the home page
 * or a search result. The `?ref=` is gone, they register clean, and the IB who
 * brought them is credited with nothing. Nobody notices, because the code was
 * never shown to anyone either.
 *
 * So the FIRST touch is remembered and used at registration even when the URL
 * no longer carries it.
 *
 * First touch wins, deliberately: the agent who actually did the work sent the
 * first link. A later link — someone else's, or the generic site — must not
 * take the credit. The only thing that overwrites it is a successful
 * registration, which clears it.
 */
const KEY = "wachsen:referral";
/** Long enough for a client who takes a fortnight to decide, short enough that
 *  a stale code cannot haunt an unrelated signup a year later. */
const TTL_DAYS = 90;

interface Stored {
  code: string;
  at: number;
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Stored;
    if (!v?.code || typeof v.at !== "number") return null;
    if (Date.now() - v.at > TTL_DAYS * 86_400_000) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return v;
  } catch {
    // Private browsing, blocked storage, corrupt value — a referral is never
    // worth breaking the page for.
    return null;
  }
}

/** Call once on app boot. Records `?ref=` the first time it is ever seen. */
export function captureReferral(): void {
  try {
    const code = new URLSearchParams(window.location.search).get("ref")?.trim().toUpperCase();
    if (!code) return;
    if (read()) return; // first touch wins
    window.localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() } satisfies Stored));
  } catch { /* ignore */ }
}

/** The code to register with: whatever is in the URL right now, else the one
 *  remembered from the first visit. */
export function referralCode(search?: string): string {
  const fromUrl = new URLSearchParams(search ?? window.location.search)
    .get("ref")?.trim().toUpperCase();
  return fromUrl || read()?.code || "";
}

/** Forget it — called once a registration has gone through. */
export function clearReferral(): void {
  try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
}
